import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import {Dialog} from 'primereact/dialog';
import {MultiSelect} from 'primereact/multiselect';
import ActionButton from './ActionButton';
import CompanyService from '../services/CompanyService';

const toCompanyOption = (company) => {
    const challengeSuffix = company.participateInGlobalChallenge ? ' - global challenge' : '';
    const externalId = company.externalId ? ` (${company.externalId})` : '';

    return {
        ...company,
        displayLabel: `${company.name}${externalId}${challengeSuffix}`,
    };
};

export const PublishToCompaniesDialog = ({body, confirmLabel, header, onHide, onPublish, visible}) => {
    const companyService = useMemo(() => new CompanyService(), []);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedExternalIds, setSelectedExternalIds] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        let active = true;

        if (!visible) {
            setSelectedExternalIds([]);
            setErrorMessage('');
            return undefined;
        }

        setLoading(true);
        setErrorMessage('');

        companyService
            .getActiveCompaniesForSuperAdmin()
            .then((response) => {
                if (!active) {
                    return;
                }

                setCompanies((response || []).map(toCompanyOption));
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                setCompanies([]);
                setErrorMessage(error?.message || 'Nie udalo sie pobrac listy firm.');
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [companyService, visible]);

    const footer = (
        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '16px'}}>
            <ActionButton className='ml-2 gray' label='Anuluj' variant='accent gray' handleClick={onHide} />
            <ActionButton
                label={confirmLabel}
                handleClick={() => onPublish(selectedExternalIds)}
                disabled={loading || selectedExternalIds.length === 0}
            />
        </div>
    );

    return (
        <Dialog
            ariaCloseIconLabel='Zamknij okno dialogowe'
            className='publish-to-companies-dialog'
            header={header}
            visible={visible}
            onHide={onHide}
            footer={footer}
            style={{width: '40rem', maxWidth: '95vw'}}
        >
            <div>
                <div>
                    <p>{body}</p>
                </div>
                <div className='form-group'>
                    <label className='p-label' htmlFor='publish-to-companies-select' style={{width: '100%'}}>
                        Firmy
                    </label>
                    <MultiSelect
                        id='publish-to-companies-select'
                        value={selectedExternalIds}
                        options={companies}
                        optionLabel='displayLabel'
                        optionValue='externalId'
                        onChange={(event) => setSelectedExternalIds(event.value || [])}
                        filter
                        showClear
                        style={{width: '100%'}}
                        placeholder={loading ? 'Ladowanie firm...' : 'Wybierz firmy'}
                        emptyMessage={loading ? 'Ladowanie firm...' : 'Brak firm do wyboru'}
                        selectedItemsLabel='Wybrano {0} firm'
                        maxSelectedLabels={3}
                    />
                </div>
                {errorMessage ? (
                    <div>
                        <small className='p-error'>{errorMessage}</small>
                    </div>
                ) : null}
            </div>
        </Dialog>
    );
};

PublishToCompaniesDialog.defaultProps = {
    body: 'Wybierz firmy, do ktorych chcesz opublikowac ten element.',
    confirmLabel: 'Publikuj',
    header: 'Publikuj do wielu firm',
};

PublishToCompaniesDialog.propTypes = {
    body: PropTypes.string,
    confirmLabel: PropTypes.string,
    header: PropTypes.string,
    onHide: PropTypes.func.isRequired,
    onPublish: PropTypes.func.isRequired,
    visible: PropTypes.bool.isRequired,
};

export default PublishToCompaniesDialog;
